import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

/**
 * The only page on the site that asks a visitor for something, and the
 * destination of 80-odd CTAs. It was a placeholder, so every "Request a
 * briefing" on the corporate site ended in nothing.
 *
 * The topic comes from the CTA (`/contact?topic=cerberus`) so the enquiry
 * arrives already labeled with what the visitor was reading.
 */

export const CONTACT_TOPICS = [
  { value: "general", label: "General enquiry" },
  { value: "cerberus", label: "Cerberus — runtime AI security" },
  { value: "tracelock", label: "TraceLock — continuous compliance" },
  { value: "sentience", label: "Sentience — regulatory change and filing" },
  { value: "bifrost-demo", label: "Bifrost — request a demo" },
  { value: "bifrost-partner", label: "Bifrost — become a partner" },
  { value: "bifrost-integration", label: "Bifrost — explore integration" },
  { value: "argus", label: "Argus — autonomous red teaming" },
  { value: "mimir", label: "Mimir" },
  { value: "guardian", label: "Guardian — human authority for AI agents" },
  { value: "guardian-pilot", label: "Guardian — request pilot access" },
  { value: "guardian-pricing", label: "Guardian Business — pilots and enterprise contracts" },
  { value: "guardian-developer", label: "Guardian — developer access" },
  { value: "guardian-embedded", label: "Guardian Embedded — design partnership" },
  { value: "guardian-evidence", label: "Guardian — security evidence request" },
  { value: "warden", label: "Warden — services and deployment" },
  { value: "briefing", label: "Executive briefing" },
  { value: "demo", label: "Guided demo" },
  { value: "research", label: "Research and disclosure" },
  { value: "press", label: "Press" },
  { value: "investors", label: "Investor inquiry" },
] as const;

type Status = "idle" | "sending" | "sent" | "error";

export function ContactPage() {
  const [params] = useSearchParams();
  const location = useLocation();
  const requested = params.get("topic") ?? "";
  const topicFromCta = CONTACT_TOPICS.some((t) => t.value === requested)
    ? requested
    : "general";

  const [topic, setTopic] = useState(topicFromCta);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Contact Us — Odingard";
  }, []);

  // A visitor can arrive at /contact?topic=warden from one CTA and then use a
  // different one without a reload; the select has to follow the URL.
  useEffect(() => setTopic(topicFromCta), [topicFromCta]);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setStatus("sending");
    setError("");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.get("name"),
          email: data.get("email"),
          company: data.get("company"),
          message: data.get("message"),
          topic: data.get("topic"),
          website: data.get("website"),
          sourcePath: `${location.pathname}${location.search}`,
          referrer: document.referrer,
        }),
      });
      const body = (await response.json().catch(() => ({}))) as {
        error?: string;
      };
      if (!response.ok) throw new Error(body.error || "");
      setStatus("sent");
      form.reset();
    } catch (caught) {
      setStatus("error");
      setError(caught instanceof Error && caught.message ? caught.message : "");
    }
  }

  if (status === "sent") {
    return (
      <main className="page contact" id="main">
        <section className="page__hero">
          <p className="page__eyebrow">Company</p>
          <h1>Thank you — we have it.</h1>
          <p className="page__lede">
            Someone from the team will reply to you directly, usually within
            one business day. If it is urgent, email{" "}
            <a href="mailto:enterprise@odingard.com">enterprise@odingard.com</a>{" "}
            and it will reach the same people faster.
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className="page contact" id="main">
      <section className="page__hero">
        <p className="page__eyebrow">Company</p>
        <h1>Talk to us</h1>
        <p className="page__lede">
          Tell us what you are trying to secure, govern or ship. Enquiries
          reach the engineers who built the product, not a routing queue.
        </p>
      </section>

      <div className="contact__layout">
        <form className="contact__form" onSubmit={submit} noValidate>
          <label>
            <span>Your name</span>
            <input name="name" type="text" autoComplete="name" required />
          </label>

          <label>
            <span>Work email</span>
            <input name="email" type="email" autoComplete="email" required />
          </label>

          <label>
            <span>
              Company <em>optional</em>
            </span>
            <input name="company" type="text" autoComplete="organization" />
          </label>

          <label>
            <span>What is this about?</span>
            <select
              name="topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            >
              {CONTACT_TOPICS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>How can we help?</span>
            <textarea name="message" rows={7} required />
          </label>

          {/* Hidden from people and from screen readers; bots fill it in. */}
          <div className="contact__trap" aria-hidden="true">
            <label>
              Website
              <input
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
              />
            </label>
          </div>

          <button
            className="btn btn--primary"
            type="submit"
            disabled={status === "sending"}
          >
            {status === "sending" ? "Sending…" : "Send enquiry"}
          </button>

          {status === "error" && (
            <p className="contact__error" role="alert">
              {error ||
                "That did not send. Please email enterprise@odingard.com and we will pick it up there."}
            </p>
          )}
        </form>

        <aside className="contact__aside">
          <h2>Other ways in</h2>
          <dl>
            <dt>Buying Cerberus</dt>
            <dd>
              Standard and Premium are priced and purchasable at{" "}
              <a href="https://cerberus.odingard.com/pricing">
                cerberus.odingard.com/pricing
              </a>
              . Use this form for four or more production environments,
              air-gapped deployments, or procurement and security review.
            </dd>

            <dt>Security disclosure</dt>
            <dd>
              Report a vulnerability to{" "}
              <a href="mailto:security@odingard.com">security@odingard.com</a>.
              Please do not put exploit detail in this form.
            </dd>

            <dt>Existing customer</dt>
            <dd>
              Support requests are fastest at{" "}
              <a href="mailto:support@odingard.com">support@odingard.com</a>.
            </dd>
          </dl>
        </aside>
      </div>
    </main>
  );
}
